import { T, formatCents, tt } from '@agos/shared'
import { cn } from '@agos/ui'
import { Loader2, Receipt, X } from 'lucide-react'

import { inFlightNote, inFlightTitle, type PaymentInFlight } from './payment-in-flight'

/**
 * The payment dialog while a payment is on its way - `FRONTEND.md` §9.769, owner O130, FB-1086.
 *
 * **Drawn from the snapshot alone.** It takes no order and reads no query, so the board's eight-second read cannot
 * reach it: the lines, the bill and *Na zaplatenie* are the ones the operator confirmed when they pressed the button,
 * until the dialog closes.
 *
 * **No control on it.** No split, no quantity, no zľava, no method, no register, no pay button and no storno - nothing
 * that could start a second payment over the first - and the header's close is disabled, because a payment in flight
 * may not be abandoned by the dialog it is in (§9.480's rule for an overlay mid-write).
 *
 * The card's own content only: `PaymentDialog` draws the overlay around it, as it does around the dialog this replaces.
 */
export function PaymentInFlightView({
  flight,
  orderLabel,
}: {
  flight: PaymentInFlight
  /** `#number · customer`, as the dialog's header reads. */
  orderLabel: string
}) {
  return (
    <div
      className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
      data-pay-in-flight={flight.kind}
      aria-busy="true"
    >
      <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
        <Receipt className="w-5 h-5 text-primary" />
        <div className="flex-1 min-w-0">
          <p className="font-bold">
            <T>Zaplatiť</T>
          </p>
          <p className="text-xs text-muted-foreground truncate">{orderLabel}</p>
        </div>
        <button
          type="button"
          aria-label={tt('Zatvoriť')}
          disabled
          className="p-1.5 rounded-lg transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1.5" data-pay-frozen-lines>
        {flight.lines.map((line) => (
          <div
            key={line.key}
            data-pay-frozen-line={line.key}
            data-pay-frozen-quantity={line.quantity}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50"
          >
            <div className="flex-1 min-w-0">
              {/* The line's own name, as the order carries it - data, so never through `tt()`. */}
              <p className="text-sm font-medium truncate">{line.name}</p>
              <p className="text-xs text-muted-foreground">{line.quantity}×</p>
            </div>
            <span className="text-sm font-bold tabular-nums">{formatCents(line.cents)}</span>
          </div>
        ))}
      </div>

      <div className="border-t border-border px-5 py-4 space-y-3 flex-shrink-0">
        {flight.rows.map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between text-xs text-muted-foreground"
            data-bill-row
          >
            <span>{row.label}</span>
            <span className={cn('tabular-nums', row.off && 'text-amber-600 dark:text-amber-400')}>
              {row.off ? '-' : ''}
              {formatCents(row.cents)}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-between font-bold">
          <span>
            <T>Na zaplatenie</T>
          </span>
          <span className="text-xl text-primary tabular-nums" data-pay-due>
            {formatCents(flight.payCents)}
          </span>
        </div>

        <div
          role="status"
          className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-sm"
          data-pay-in-flight-note
        >
          <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin text-primary" />
          <div>
            <p className="font-bold text-primary">
              {inFlightTitle()} <span className="tabular-nums">{formatCents(flight.payCents)}</span>
            </p>
            <p className="text-xs text-muted-foreground">{inFlightNote(flight.kind)}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
